// Module H · Global search palette UI.
//
// Mounted at the App root via <GlobalSearchProvider />. Renders nothing
// until opened (Cmd/Ctrl+K, "/", or the topbar button). Keyboard-driven:
// ↑↓ move, Enter navigates, Esc closes. Mobile-safe: full-width modal,
// 44px tap targets, no horizontal overflow.

import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
} from "react";
import { useNavigate } from "react-router-dom";
import {
  MagnifyingGlass, X, Clock, ArrowRight,
  Users, Baby, ChalkboardTeacher, Receipt, FirstAid, Flag,
  FileText, Megaphone,
} from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { ROLES } from "@/lib/roles";
import {
  flattenResults,
  KIND_META,
  loadRecentSearches,
  pushRecentSearch,
  useGlobalSearch,
  useNonEmptyKinds,
  usePaletteHotkeys,
} from "@/lib/globalSearch";

const ICON_MAP = {
  Users, Baby, ChalkboardTeacher, Receipt, FirstAid, Flag, FileText, Megaphone,
};

// ─── Context ─────────────────────────────────────────────────────────

const PaletteContext = createContext({ open: () => {}, close: () => {} });

export function useGlobalSearchPalette() {
  return useContext(PaletteContext);
}

export function GlobalSearchProvider({ children }) {
  const { role, profile } = useAuth();
  const [open, setOpen] = useState(false);
  const allowed = role === ROLES.ADMIN || role === ROLES.CMS_EDITOR;
  const restoreFocusRef = useRef(null);

  const onOpen = useCallback(() => {
    if (!allowed) return;
    restoreFocusRef.current = document.activeElement;
    setOpen(true);
  }, [allowed]);
  const onClose = useCallback(() => {
    setOpen(false);
    window.requestAnimationFrame(() => restoreFocusRef.current?.focus?.({ preventScroll: true }));
  }, []);

  usePaletteHotkeys(onOpen);

  return (
    <PaletteContext.Provider value={{ open: onOpen, close: onClose, allowed }}>
      {children}
      {allowed && open && (
        <PaletteModal role={role} profileId={profile?.id} onClose={onClose} />
      )}
    </PaletteContext.Provider>
  );
}

// ─── Modal ───────────────────────────────────────────────────────────

function PaletteModal({ role, onClose }) {
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const [query, setQuery] = useState("");
  const [recent, setRecent] = useState(() => loadRecentSearches());
  const [activeIdx, setActiveIdx] = useState(0);
  const { results, loading } = useGlobalSearch(query, { maxPerKind: 5 });

  const flat = useMemo(() => flattenResults(results), [results]);
  const nonEmpty = useNonEmptyKinds(results);

  // Auto-focus the input on mount; reset highlight on new results.
  useEffect(() => { inputRef.current?.focus(); }, []);
  useEffect(() => { setActiveIdx(0); }, [query, flat.length]);

  // Lock body scroll while open (mobile palette feel).
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  const onPick = useCallback((flatRow) => {
    if (!flatRow?.path) return;
    pushRecentSearch(query);
    setRecent(loadRecentSearches());
    onClose();
    navigate(flatRow.path);
  }, [query, navigate, onClose]);

  const onKeyDown = (e) => {
    if (e.key === "Escape") {
      e.preventDefault();
      onClose();
      return;
    }
    if (!flat.length) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, flat.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      onPick(flat[activeIdx]);
    }
  };

  // CMS users see a soft hint that family/operational data isn't shown.
  const cmsScopeHint = role === ROLES.CMS_EDITOR;

  return (
    <div
      data-testid="global-search-palette"
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 px-3 pt-[10vh] sm:px-6"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      role="dialog"
      aria-modal="true"
      aria-label="Global search"
    >
      <div
        className="w-full max-w-xl overflow-hidden rounded-2xl bg-white shadow-2xl"
        onKeyDown={onKeyDown}
      >
        {/* Input row */}
        <div className="flex items-center gap-3 border-b border-neutral-200 px-4 py-3">
          <MagnifyingGlass size={18} weight="duotone" className="flex-shrink-0 text-neutral-500" />
          <input
            ref={inputRef}
            data-testid="global-search-input"
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={
              cmsScopeHint
                ? "Search CMS pages, announcements…"
                : "Search families, children, staff, invoices, incidents…"
            }
            className="min-w-0 flex-1 bg-transparent text-base font-medium text-neutral-900 placeholder:text-neutral-400 focus:outline-none"
            aria-label="Search query"
          />
          {query && (
            <button
              type="button"
              data-testid="global-search-clear"
              onClick={() => { setQuery(""); inputRef.current?.focus(); }}
              aria-label="Clear search"
              className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-neutral-400 hover:bg-neutral-100 hover:text-neutral-700"
            >
              <X size={12} weight="bold" />
            </button>
          )}
          <span className="hidden flex-shrink-0 rounded border border-neutral-200 bg-neutral-50 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-neutral-500 sm:inline">
            Esc
          </span>
        </div>

        {/* Body */}
        <div className="max-h-[60vh] overflow-y-auto">
          {!query && recent.length > 0 && (
            <RecentList
              recent={recent}
              onPick={(q) => setQuery(q)}
            />
          )}
          {!query && recent.length === 0 && (
            <EmptyHint cmsScopeHint={cmsScopeHint} />
          )}

          {query && loading && (
            <div className="flex items-center justify-center py-8">
              <div className="h-6 w-6 rounded-full border-2 border-primary/30 border-t-primary animate-spin" />
            </div>
          )}

          {query && !loading && flat.length === 0 && (
            <div
              data-testid="global-search-empty"
              className="px-6 py-10 text-center text-sm text-neutral-500"
            >
              No matches for <span className="font-semibold text-neutral-700">{query}</span>.
            </div>
          )}

          {query && !loading && flat.length > 0 && (
            <ResultsList
              results={results}
              nonEmpty={nonEmpty}
              flat={flat}
              activeIdx={activeIdx}
              onHover={setActiveIdx}
              onPick={onPick}
            />
          )}
        </div>

        {/* Footer hints */}
        <footer className="hidden items-center justify-between border-t border-neutral-200 bg-neutral-50 px-4 py-2 sm:flex">
          <span className="text-[10px] font-medium uppercase tracking-wider text-neutral-500">
            <Hint>↑↓</Hint> navigate <Hint className="ml-2">⏎</Hint> open <Hint className="ml-2">esc</Hint> close
          </span>
          {cmsScopeHint && (
            <span className="text-[10px] italic text-neutral-400">
              CMS scope · operational records hidden
            </span>
          )}
        </footer>
      </div>
    </div>
  );
}

function Hint({ children, className = "" }) {
  return (
    <span className={`rounded border border-neutral-300 bg-white px-1.5 font-bold ${className}`}>
      {children}
    </span>
  );
}

function RecentList({ recent, onPick }) {
  return (
    <div data-testid="global-search-recent" className="px-2 py-2">
      <p className="px-3 py-1 text-[10px] font-bold uppercase tracking-[0.15em] text-neutral-500">
        Recent
      </p>
      <ul>
        {recent.map((q) => (
          <li key={q}>
            <button
              type="button"
              onClick={() => onPick(q)}
              data-testid={`global-search-recent-${q.replace(/\s+/g, "-").slice(0, 30)}`}
              className="flex w-full items-center gap-3 rounded-xl px-3 py-2 text-left text-sm text-neutral-700 hover:bg-neutral-100"
            >
              <Clock size={14} weight="duotone" className="flex-shrink-0 text-neutral-400" />
              <span className="truncate">{q}</span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function EmptyHint({ cmsScopeHint }) {
  return (
    <div
      data-testid="global-search-empty-hint"
      className="px-6 py-10 text-center text-sm text-neutral-500"
    >
      <p className="font-semibold text-neutral-700">Quick search</p>
      <p className="mt-1 text-xs leading-relaxed">
        {cmsScopeHint
          ? "Search CMS pages and announcements. Operational records are hidden for CMS editors."
          : "Find families, children, staff, invoices, incidents, inquiries, and CMS content."}
      </p>
    </div>
  );
}

function ResultsList({ results, nonEmpty, flat, activeIdx, onHover, onPick }) {
  // Compute the absolute index of each rendered row so the highlight
  // matches the keyboard cursor exactly.
  let absIdx = -1;
  return (
    <ul data-testid="global-search-results" className="px-2 py-2">
      {nonEmpty.map((kind) => {
        const items = results[kind] || [];
        const meta = KIND_META[kind];
        const Icon = ICON_MAP[meta?.icon] || MagnifyingGlass;
        return (
          <li key={kind} className="mb-2 last:mb-0">
            <p
              data-testid={`global-search-section-${kind}`}
              className="px-3 py-1 text-[10px] font-bold uppercase tracking-[0.15em] text-neutral-500"
            >
              {meta?.label || kind}
            </p>
            <ul>
              {items.map((item) => {
                absIdx += 1;
                const flatRow = flat[absIdx];
                const isActive = absIdx === activeIdx;
                return (
                  <li key={`${kind}-${item.id}`}>
                    <button
                      type="button"
                      data-testid={`global-search-row-${kind}-${item.id}`}
                      data-active={isActive ? "true" : "false"}
                      onMouseEnter={() => onHover(absIdx)}
                      onClick={() => onPick(flatRow)}
                      className={`flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left transition ${
                        isActive
                          ? "bg-primary/10 text-primary-hover"
                          : "text-neutral-800 hover:bg-neutral-100"
                      }`}
                    >
                      <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-neutral-100">
                        <Icon size={14} weight="duotone" />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-semibold">
                          {item.label || "—"}
                        </span>
                        {item.sub && (
                          <span className="block truncate text-[11px] text-neutral-500">
                            {item.sub}
                          </span>
                        )}
                      </span>
                      <ArrowRight
                        size={12}
                        weight="bold"
                        className={`flex-shrink-0 transition ${
                          isActive ? "text-primary-hover" : "text-neutral-300"
                        }`}
                      />
                    </button>
                  </li>
                );
              })}
            </ul>
          </li>
        );
      })}
    </ul>
  );
}

// ─── Topbar trigger button ───────────────────────────────────────────

export function GlobalSearchTriggerButton({ className = "" }) {
  const { open } = useGlobalSearchPalette();
  return (
    <button
      type="button"
      data-testid="desktop-shell-search-button"
      onClick={open}
      title="Search · Cmd K"
      className={`flex w-full items-center gap-2 rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm text-neutral-500 transition hover:border-primary/40 hover:bg-primary/[0.02] ${className}`}
    >
      <MagnifyingGlass size={14} weight="duotone" className="flex-shrink-0" />
      <span className="flex-1 text-left">Search…</span>
      <span className="hidden rounded border border-neutral-200 bg-neutral-50 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider sm:inline">
        ⌘K
      </span>
    </button>
  );
}
